import { Suspense } from 'react';
import { Loader2 } from 'lucide-react';
import { Routes, Route } from 'react-router-dom';

import { AppShell } from '@/components/layout/AppShell';
import { apiKeyRoutes } from '@/features/apikeys';
import { assessmentRoutes } from '@/features/assessments';
import { authRoutes, RequireAuth } from '@/features/auth';
import { billingRoutes } from '@/features/billing';
import { classRoutes, joinClassRoutes } from '@/features/classes';
import { announcementRoutes, chatRoutes, moderationRoutes } from '@/features/communication';
import { curriculumRoutes } from '@/features/curriculum';
import { dashboardRoutes } from '@/features/dashboard';
import { homeworkRoutes } from '@/features/homework';
import { inviteRoutes } from '@/features/invitations';
import { libraryRoutes } from '@/features/library';
import { memberRoutes } from '@/features/members';
import { notificationRoutes } from '@/features/notifications';
import { progressRoutes } from '@/features/progress';
import { projectRoutes } from '@/features/projects';
import { RequireSchool, schoolRoutes } from '@/features/schools';
import { settingsRoutes } from '@/features/settings';
import { NotFound } from '@/pages/NotFound';

function RouteFallback() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center">
      <Loader2 className="text-muted-foreground size-6 animate-spin" />
    </div>
  );
}

/**
 * Route composition ONLY (FE-ADR-001). Each feature owns its routes in
 * `features/<x>/routes.tsx` (lazy-loaded); this file just assembles the tree:
 *
 *   public (auth pages)
 *   └─ RequireAuth
 *      ├─ school onboarding (full-page, outside the shell)
 *      └─ RequireSchool → AppShell → feature routes
 */
export default function App() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {authRoutes}
        {inviteRoutes}
        <Route element={<RequireAuth />}>
          {schoolRoutes}
          {joinClassRoutes}
          <Route element={<RequireSchool />}>
            <Route element={<AppShell />}>
              {dashboardRoutes}
              {curriculumRoutes}
              {assessmentRoutes}
              {homeworkRoutes}
              {classRoutes}
              {chatRoutes}
              {announcementRoutes}
              {moderationRoutes}
              {memberRoutes}
              {notificationRoutes}
              {progressRoutes}
              {projectRoutes}
              {libraryRoutes}
              {settingsRoutes}
              {billingRoutes}
              {apiKeyRoutes}
              <Route path="*" element={<NotFound />} />
            </Route>
          </Route>
        </Route>
      </Routes>
    </Suspense>
  );
}
